// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { UnsavedChangesDialog } from '@/components/unsaved-changes-dialog';
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { getItem } from '@/items/api/get-item';
import { itemBomFormSchema } from '@/items/api/types';
import { updateItemBOM } from '@/items/api/update-item-bom';
import { ItemBOMForm, ItemBOMFormRef } from '@/items/components/item-bom-form';
import { ItemBOMFormSkeleton } from '@/items/components/item-bom-form-skeleton';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Collapse, Stack } from '@mui/material';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Navigate, createFileRoute, useBlocker } from '@tanstack/react-router';
import { Suspense, useRef } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

export const Route = createFileRoute('/_app/catalog/items/$itemKey/bom')({
  component: ItemBOMRoute,
});

const formSchema = z.object({
  bom: itemBomFormSchema,
});

type FormData = z.infer<typeof formSchema>;

function ItemBOMRoute() {
  const { itemKey } = Route.useParams();
  const featureFlags = useFeatureFlags();

  // Check if this item type supports BOM
  const { data: itemData } = useSuspenseQuery({
    queryKey: ['item', itemKey],
    queryFn: () => getItem(itemKey),
  });

  // Only DEVICE items with enableBOM feature flag can have BOM
  if (!itemData.item.hasAssets || !featureFlags.enableBOM) {
    return (
      <Navigate to="/catalog/items/$itemKey" params={{ itemKey }} replace />
    );
  }

  return <ItemBOMContent itemKey={itemKey} />;
}

function ItemBOMContent(props: { itemKey: string }) {
  const queryClient = useQueryClient();
  const formRef = useRef<ItemBOMFormRef>(null);

  // Fetch item data for the BOM form
  const { data } = useSuspenseQuery({
    queryKey: ['item', props.itemKey, 'bom'],
    queryFn: () => getItem(props.itemKey),
  });

  // BOM data from API is already transformed: { quantity, item } via schema
  const initialBom = data.item.bom ?? [];

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bom: initialBom,
    },
  });

  const { handleSubmit, formState, reset, resetField } = methods;
  const { isDirty } = formState;

  const blocker = useBlocker({ condition: isDirty });
  const isBlocked = blocker.status === 'blocked';

  const handleClickReset = () => {
    resetField('bom', { defaultValue: initialBom });
  };

  const updateBomMutation = useMutation({
    mutationFn: (bom: FormData['bom']) => {
      const formattedBom = bom
        .filter((lineItem) => lineItem.item !== null)
        .map((lineItem) => ({
          itemId: lineItem.item!.id,
          quantity: lineItem.quantity,
        }));
      return updateItemBOM(props.itemKey, { bom: formattedBom });
    },
    onSuccess: (responseData) => {
      queryClient.invalidateQueries({ queryKey: ['item'] });
      // BOM data from API is already transformed: { quantity, item } via schema
      reset({ bom: responseData.item.bom ?? [] });
    },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    updateBomMutation.mutate(data.bom);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleValidSubmission)}>
        <UnsavedChangesDialog
          isOpen={isBlocked}
          onCancel={blocker.reset}
          onProceed={blocker.proceed}
        />
        <HardfinTabCardWrapper
          title="Bill of materials"
          actions={
            <Button
              variant="contained"
              color="secondary"
              onClick={() => formRef.current?.addLineItem()}
              startIcon={<AddRounded fontSize="small" />}
            >
              Add item
            </Button>
          }
        >
          <Stack paddingTop={2}>
            <Suspense fallback={<ItemBOMFormSkeleton />}>
              <ItemBOMForm ref={formRef} name="bom" />
            </Suspense>
            <Collapse in={isDirty}>
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1}
                paddingTop={2}
                paddingBottom={1}
              >
                <Button onClick={handleClickReset}>Reset</Button>
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={updateBomMutation.isPending}
                >
                  Save changes
                </Button>
              </Stack>
            </Collapse>
          </Stack>
        </HardfinTabCardWrapper>
      </form>
    </FormProvider>
  );
}
